// Pointer Events vereinheitlichen Maus und Touch.
// 1 Finger auf Teil = Verschieben auf der Arbeitsebene (XZ), sonst Orbit (OrbitControls).
// Tap auf Teil = Selektion, Tap ins Leere = Deselektion.
import { snapToGrid, snapToFaces, snapAngle } from "../snapping"
import { resizeByEdge, edgeOutwardWorld } from "../resize"
import { rotationDeltaDeg } from "../gizmo"

const TAP_MAX_MS = 350
const TAP_MAX_PX = 12

export default class PointerInput {
  constructor(canvas, { renderer, document: doc, onSelect, onMoveEnd, onMovePreview, onDoubleTap, onHandleTap, onEdgeResize, onRotateEnd, grid = () => 10 }) {
    this.onHandleTap = onHandleTap
    this.onEdgeResize = onEdgeResize
    this.onRotateEnd = onRotateEnd
    this.edgeDrag = null
    this.rotateDrag = null
    this.canvas = canvas
    this.renderer = renderer
    this.doc = doc
    this.onSelect = onSelect
    this.onMoveEnd = onMoveEnd
    this.onMovePreview = onMovePreview
    this.onDoubleTap = onDoubleTap
    this.grid = grid
    this.pickHandler = null // Pick-Modus: nächster Teil-Tap geht hierhin statt in Selektion/Drag
    this.sketch = null // Skizzen-Modus: {down, move, up, tap, capturesPointer, abortStroke} mit Bodenpunkten
    this.sketchPointerId = null
    this.sketchTapDown = null
    this.sketchLastClient = null // letzte Fingerposition des Zeichen-Strichs
    this.sketchPinch = null // aktiver Zwei-Finger-Zoom in der Skizze
    this.measureHandler = null // Mess-Modus: bekommt Oberflächenpunkte
    this.measureTapDown = null // Tap-Kandidat im Mess-Modus (Drag = Orbit)
    this.lastTap = null // { time, clientId } für Doppeltipp-Erkennung
    this.partTapDown = null // Tap-Kandidat auf nicht selektiertem Teil
    this.drag = null
    this.activePointers = new Set()

    canvas.addEventListener("pointerdown", (e) => this.#down(e))
    canvas.addEventListener("pointermove", (e) => this.#move(e))
    canvas.addEventListener("pointerup", (e) => this.#up(e))
    canvas.addEventListener("pointercancel", (e) => this.#cancel(e))
  }

  #down(event) {
    this.activePointers.add(event.pointerId)

    // Zweiter Finger: Drag abbrechen, OrbitControls übernehmen (Pinch/Pan)
    if (this.activePointers.size > 1) {
      this.#abortDrag()
      this.#abortEdgeDrag()
      this.#abortRotateDrag()
      this.partTapDown = null
      this.measureTapDown = null
      // Aktiver Zeichen-Strich: OrbitControls haben den ersten Finger nie
      // gesehen (Pointer-Capture) und können keinen Pinch aufbauen —
      // Strich verwerfen und den Zoom selbst übernehmen
      if (this.sketch && this.sketchPointerId !== null) {
        this.sketch.abortStroke?.()
        try { this.canvas.releasePointerCapture(this.sketchPointerId) } catch { /* synthetische Events */ }
        this.sketchPinch = new Map([
          [ this.sketchPointerId, this.sketchLastClient ?? { x: event.clientX, y: event.clientY } ],
          [ event.pointerId, { x: event.clientX, y: event.clientY } ]
        ])
        this.sketchPointerId = null
        this.sketchTapDown = null
        return
      }
      if (this.sketchPointerId !== null) this.renderer.setOrbitEnabled(true)
      this.sketchPointerId = null
      this.sketchTapDown = null
      this.sketch?.abortStroke?.()
      return
    }

    // Skizzen-Modus: Rechteck/Kreis ziehen mit einem Finger (Orbit solange
    // aus); Wählen/Polygon arbeiten mit Taps — ein Drag dreht die Kamera
    if (this.sketch) {
      // Ebenen-Wahl: Tap auf eine Teil-Oberfläche liefert den 3D-Punkt
      if (this.sketch.pickingSurface?.()) {
        const hit = this.renderer.surfaceHitAt(event)
        if (hit) this.sketch.surfaceTap?.(hit)
        return
      }
      const point = this.renderer.sketchPlanePointAt(event, this.sketch.plane())
      if (!point) return
      if (this.sketch.capturesPointer?.()) {
        this.sketchPointerId = event.pointerId
        this.sketchLastClient = { x: event.clientX, y: event.clientY }
        this.#capture(event)
        this.sketch.down({ x: point.x, z: point.z })
      } else {
        this.sketchTapDown = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now() }
      }
      return
    }

    // Mess-Modus: nur echte Taps setzen einen Punkt (Entscheidung beim up),
    // ein Drag bleibt Orbit — die Ansicht ist beim Messen frei drehbar
    if (this.measureHandler) {
      this.measureTapDown = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now() }
      return
    }

    // Handles haben Vorrang vor Teil-Selektion und Drag.
    // Kante: Ziehen = Resize, Tippen = Kontextleiste. Hebe-Pfeil = senkrecht
    // verschieben, Ring = um die Hochachse drehen (15°-Raster).
    if (!this.pickHandler) {
      const handle = this.renderer.pickHandle?.(event)
      if (handle) {
        const part = this.doc.getPart(this.renderer.selectedId)

        if (handle.type === "edge" && part) {
          const floorPoint = this.renderer.floorPointAt(event, part.pos_y)
          if (floorPoint) {
            this.#capture(event)
            this.renderer.setDragHandle?.({ type: "edge", key: handle.key })
            this.edgeDrag = {
              pointerId: event.pointerId,
              handle,
              clientId: part.client_id,
              startFloor: floorPoint,
              startClient: { x: event.clientX, y: event.clientY },
              moved: false,
              attrs: null
            }
            return
          }
        } else if (handle.type === "axis" && part) {
          const axis = handle.key
          const startRef = axis === "y"
            ? this.renderer.verticalPointAt(event, { x: part.pos_x, y: part.pos_y, z: part.pos_z })
            : this.renderer.floorPointAt(event, part.pos_y)
          if (startRef !== null && startRef !== undefined) {
            this.#capture(event)
            this.renderer.setDragHandle?.({ type: "axis", key: axis })
            this.drag = {
              kind: "axis",
              axis,
              pointerId: event.pointerId,
              clientId: part.client_id,
              startPart: { x: part.pos_x, y: part.pos_y, z: part.pos_z },
              startRef,
              startClient: { x: event.clientX, y: event.clientY },
              moved: false,
              current: null
            }
            return
          }
        } else if (handle.type === "rotate" && part) {
          const axis = handle.key.slice(4) // "rot-x" → "x"
          const startPoint = this.renderer.planePointAt(event, { x: part.pos_x, y: part.pos_y, z: part.pos_z }, axis)
          if (startPoint) {
            this.#capture(event)
            this.renderer.setDragHandle?.({ type: "rotate", key: handle.key })
            this.rotateDrag = {
              pointerId: event.pointerId,
              clientId: part.client_id,
              axis,
              startRot: { x: part.rot_x, y: part.rot_y, z: part.rot_z },
              startVector: {
                x: startPoint.x - part.pos_x,
                y: startPoint.y - part.pos_y,
                z: startPoint.z - part.pos_z
              },
              startClient: { x: event.clientX, y: event.clientY },
              moved: false,
              attrs: null
            }
            return
          }
        }

        if (handle.type === "corner" || handle.type === "edge") this.onHandleTap?.(handle)
        return
      }
    }

    const clientId = this.renderer.pickPart(event)
    if (!clientId) {
      // leerer Raum → OrbitControls orbiten; Tap-Kandidat für Deselektion merken
      this.emptyDown = { x: event.clientX, y: event.clientY, time: performance.now() }
      return
    }

    if (this.pickHandler) {
      this.pickHandler(clientId)
      return
    }

    // Nicht selektierte Teile: erster Tap selektiert nur (auf pointerup),
    // Ziehen darüber orbitiert die Kamera — kein versehentliches Verschieben
    if (clientId !== this.renderer.selectedId) {
      this.partTapDown = { clientId, x: event.clientX, y: event.clientY, time: performance.now() }
      return
    }

    const part = this.doc.getPart(clientId)
    const floorPoint = this.renderer.floorPointAt(event, part.pos_y)
    if (!floorPoint) return

    this.#capture(event)
    this.drag = {
      kind: "move",
      pointerId: event.pointerId,
      clientId,
      startPart: { x: part.pos_x, y: part.pos_y, z: part.pos_z },
      startFloor: floorPoint,
      startClient: { x: event.clientX, y: event.clientY },
      startTime: performance.now(),
      moved: false,
      current: null
    }
  }

  #capture(event) {
    this.renderer.setOrbitEnabled(false)
    try { this.canvas.setPointerCapture(event.pointerId) } catch { /* synthetische Events */ }
  }

  #move(event) {
    if (this.sketchPinch?.has(event.pointerId)) {
      const partnerId = [ ...this.sketchPinch.keys() ].find((id) => id !== event.pointerId)
      if (partnerId !== undefined) {
        const prev = this.sketchPinch.get(event.pointerId)
        const partner = this.sketchPinch.get(partnerId)
        const oldDist = Math.hypot(prev.x - partner.x, prev.y - partner.y)
        const newDist = Math.hypot(event.clientX - partner.x, event.clientY - partner.y)
        if (oldDist > 8 && newDist > 8) this.renderer.zoomBy(oldDist / newDist)
      }
      this.sketchPinch.set(event.pointerId, { x: event.clientX, y: event.clientY })
      return
    }
    if (this.sketch && event.pointerId === this.sketchPointerId) {
      this.sketchLastClient = { x: event.clientX, y: event.clientY }
      const point = this.renderer.sketchPlanePointAt(event, this.sketch.plane())
      if (point) this.sketch.move({ x: point.x, z: point.z })
      return
    }
    if (this.edgeDrag && event.pointerId === this.edgeDrag.pointerId) {
      this.#edgeDragMove(event)
      return
    }
    if (this.rotateDrag && event.pointerId === this.rotateDrag.pointerId) {
      this.#rotateMove(event)
      return
    }
    if (!this.drag || event.pointerId !== this.drag.pointerId) return

    const dx = event.clientX - this.drag.startClient.x
    const dy = event.clientY - this.drag.startClient.y
    if (!this.drag.moved && Math.hypot(dx, dy) < TAP_MAX_PX) return
    this.drag.moved = true

    if (this.drag.kind === "axis") {
      this.#axisMove(event)
      return
    }

    const floorPoint = this.renderer.floorPointAt(event, this.drag.startPart.y)
    if (!floorPoint) return

    // Flächen-Snap (bündig andocken) hat Vorrang, sonst Raster-Snap je Achse
    const raw = {
      pos_x: this.drag.startPart.x + (floorPoint.x - this.drag.startFloor.x),
      pos_y: this.drag.startPart.y,
      pos_z: this.drag.startPart.z + (floorPoint.z - this.drag.startFloor.z)
    }
    const part = this.doc.getPart(this.drag.clientId)
    const faces = snapToFaces(part, raw, this.doc.allParts())
    const grid = this.grid()
    this.drag.current = {
      pos_x: faces.snappedX ? faces.pos_x : snapToGrid(raw.pos_x, grid),
      pos_y: raw.pos_y,
      pos_z: faces.snappedZ ? faces.pos_z : snapToGrid(raw.pos_z, grid)
    }
    if (this.onMovePreview) this.onMovePreview(this.drag.clientId, this.drag.current)
    else this.renderer.setPreviewPosition(this.drag.clientId, this.drag.current)
    // Snap-Feedback: bündig eingerastet = grünes Highlight
    this.renderer.setSnapActive?.(faces.snappedX || faces.snappedZ)
  }

  // Achsen-Pfeil: exakt entlang einer Weltachse verschieben.
  // Y-Flächen-Snap stapelt bündig, X/Z docken seitlich an.
  #axisMove(event) {
    const drag = this.drag
    const part = this.doc.getPart(drag.clientId)
    if (!part) return

    let delta
    if (drag.axis === "y") {
      const y = this.renderer.verticalPointAt(event, { x: drag.startPart.x, y: drag.startPart.y, z: drag.startPart.z })
      if (y === null) return
      delta = y - drag.startRef
    } else {
      const floorPoint = this.renderer.floorPointAt(event, drag.startPart.y)
      if (!floorPoint) return
      delta = drag.axis === "x" ? floorPoint.x - drag.startRef.x : floorPoint.z - drag.startRef.z
    }

    const posKey = { x: "pos_x", y: "pos_y", z: "pos_z" }[drag.axis]
    const raw = {
      pos_x: drag.startPart.x,
      pos_y: drag.startPart.y,
      pos_z: drag.startPart.z,
      [posKey]: drag.startPart[drag.axis] + delta
    }
    const faces = snapToFaces(part, raw, this.doc.allParts(), 8, [ drag.axis ])
    const snapped = faces.snappedX || faces.snappedY || faces.snappedZ
    drag.current = { ...raw, [posKey]: snapped ? faces[posKey] : snapToGrid(raw[posKey], this.grid()) }

    if (this.onMovePreview) this.onMovePreview(drag.clientId, drag.current)
    else this.renderer.setPreviewPosition(drag.clientId, drag.current)
    this.renderer.setSnapActive?.(snapped)
  }

  // Ring-Drag: das Teil folgt dem Finger in der Ring-Ebene, 15°-Raster
  #rotateMove(event) {
    const drag = this.rotateDrag
    const part = this.doc.getPart(drag.clientId)
    if (!part) return

    if (!drag.moved) {
      const distance = Math.hypot(event.clientX - drag.startClient.x, event.clientY - drag.startClient.y)
      if (distance < TAP_MAX_PX) return
      drag.moved = true
    }

    const point = this.renderer.planePointAt(event, { x: part.pos_x, y: part.pos_y, z: part.pos_z }, drag.axis)
    if (!point) return

    const vector = { x: point.x - part.pos_x, y: point.y - part.pos_y, z: point.z - part.pos_z }
    const deltaDeg = rotationDeltaDeg(drag.axis, drag.startVector, vector)
    const rotField = { x: "rot_x", y: "rot_y", z: "rot_z" }[drag.axis]
    drag.attrs = { [rotField]: snapAngle(drag.startRot[drag.axis] + deltaDeg, 15) }
    this.renderer.setPreviewRotation(drag.clientId, {
      rot_x: drag.startRot.x, rot_y: drag.startRot.y, rot_z: drag.startRot.z, ...drag.attrs
    })
  }

  #edgeDragMove(event) {
    const drag = this.edgeDrag
    const part = this.doc.getPart(drag.clientId)
    if (!part) return

    if (!drag.moved) {
      const distance = Math.hypot(event.clientX - drag.startClient.x, event.clientY - drag.startClient.y)
      if (distance < TAP_MAX_PX) return
      drag.moved = true
    }

    const floorPoint = this.renderer.floorPointAt(event, part.pos_y)
    if (!floorPoint) return

    const outward = edgeOutwardWorld(part, drag.handle.key)
    const delta = (floorPoint.x - drag.startFloor.x) * outward[0] +
                  (floorPoint.z - drag.startFloor.z) * outward[2]
    drag.attrs = resizeByEdge(part, drag.handle.key, delta, this.grid())

    const preview = drag.attrs ?? { pos_x: part.pos_x, pos_y: part.pos_y, pos_z: part.pos_z }
    this.renderer.setPreviewResize(drag.clientId, {
      x: drag.attrs?.length_mm ?? part.length_mm,
      y: part.thickness_mm,
      z: drag.attrs?.width_mm ?? part.width_mm
    }, preview)
  }

  #up(event) {
    if (this.measureHandler && this.measureTapDown?.pointerId === event.pointerId) {
      this.activePointers.delete(event.pointerId)
      const down = this.measureTapDown
      this.measureTapDown = null
      const quick = performance.now() - down.time < TAP_MAX_MS
      const close = Math.hypot(event.clientX - down.x, event.clientY - down.y) < TAP_MAX_PX
      if (quick && close) {
        const point = this.renderer.surfacePointAt(event)
        if (point) this.measureHandler(point)
      }
      return
    }
    if (this.sketchPinch?.has(event.pointerId)) {
      this.activePointers.delete(event.pointerId)
      this.sketchPinch.delete(event.pointerId)
      if (this.sketchPinch.size === 0) {
        this.sketchPinch = null
        this.renderer.setOrbitEnabled(true)
      }
      return
    }
    if (this.sketch && event.pointerId === this.sketchPointerId) {
      this.activePointers.delete(event.pointerId)
      this.sketchPointerId = null
      this.renderer.setOrbitEnabled(true)
      const point = this.renderer.sketchPlanePointAt(event, this.sketch.plane())
      this.sketch.up(point ? { x: point.x, z: point.z } : null)
      return
    }
    if (this.sketch && this.sketchTapDown?.pointerId === event.pointerId) {
      this.activePointers.delete(event.pointerId)
      const down = this.sketchTapDown
      this.sketchTapDown = null
      const quick = performance.now() - down.time < TAP_MAX_MS
      const close = Math.hypot(event.clientX - down.x, event.clientY - down.y) < TAP_MAX_PX
      if (quick && close) {
        const point = this.renderer.sketchPlanePointAt(event, this.sketch.plane())
        if (point) this.sketch.tap?.({ x: point.x, z: point.z })
      }
      return
    }
    if (this.rotateDrag && event.pointerId === this.rotateDrag.pointerId) {
      this.activePointers.delete(event.pointerId)
      const { clientId, moved, attrs, startRot } = this.rotateDrag
      this.rotateDrag = null
      this.renderer.setOrbitEnabled(true)
      this.renderer.setDragHandle?.(null)
      if (moved && attrs) this.onRotateEnd?.(clientId, attrs)
      else this.renderer.setPreviewRotation(clientId, { rot_x: startRot.x, rot_y: startRot.y, rot_z: startRot.z })
      return
    }
    if (this.edgeDrag && event.pointerId === this.edgeDrag.pointerId) {
      this.activePointers.delete(event.pointerId)
      const { handle, clientId, moved, attrs } = this.edgeDrag
      this.edgeDrag = null
      this.renderer.setOrbitEnabled(true)
      this.renderer.setDragHandle?.(null)
      if (!moved) this.onHandleTap?.(handle)
      else if (attrs) this.onEdgeResize?.(clientId, attrs)
      else this.#restoreResizePreview(clientId)
      return
    }
    this.activePointers.delete(event.pointerId)
    if (!this.drag || event.pointerId !== this.drag.pointerId) {
      if (this.activePointers.size === 0 && !this.drag) {
        if (this.partTapDown) this.#partTap(event)
        else this.#emptyTap(event)
      }
      this.partTapDown = null
      this.emptyDown = null
      return
    }

    const { clientId, current, moved } = this.drag
    this.drag = null
    this.renderer.setOrbitEnabled(true)
    this.renderer.setSnapActive?.(false)
    this.renderer.setDragHandle?.(null)

    if (moved && current) this.onMoveEnd(clientId, current)
    else if (!moved) this.#selectedTap(clientId)
  }

  // Tap auf ein nicht selektiertes Teil → nur Selektion (Doppeltipp-Basis)
  #partTap(event) {
    const down = this.partTapDown
    const now = performance.now()
    const quick = now - down.time < TAP_MAX_MS
    const close = Math.hypot(event.clientX - down.x, event.clientY - down.y) < TAP_MAX_PX
    if (!quick || !close) return
    this.lastTap = { time: now, clientId: down.clientId }
    this.onSelect(down.clientId)
  }

  // Tap (ohne Bewegung) auf das bereits selektierte Teil → Doppeltipp-Fokus
  #selectedTap(clientId) {
    const now = performance.now()
    if (this.lastTap && now - this.lastTap.time < 350 && this.lastTap.clientId === clientId) {
      this.lastTap = null
      this.onDoubleTap?.(clientId)
    } else {
      this.lastTap = { time: now, clientId }
    }
  }

  #emptyTap(event) {
    if (!this.emptyDown) return
    const now = performance.now()
    const shortEnough = now - this.emptyDown.time < TAP_MAX_MS
    const closeEnough = Math.hypot(event.clientX - this.emptyDown.x, event.clientY - this.emptyDown.y) < TAP_MAX_PX
    if (!shortEnough || !closeEnough) return

    // Doppeltipp ins Leere = Gesamtansicht
    if (this.lastTap && now - this.lastTap.time < 350 && this.lastTap.clientId === null) {
      this.lastTap = null
      this.onDoubleTap?.(null)
      return
    }
    this.lastTap = { time: now, clientId: null }
    this.onSelect(null)
  }

  #restoreResizePreview(clientId) {
    const part = this.doc.getPart(clientId)
    if (!part) return
    this.renderer.setPreviewResize(clientId,
      { x: part.length_mm, y: part.thickness_mm, z: part.width_mm },
      { pos_x: part.pos_x, pos_y: part.pos_y, pos_z: part.pos_z })
  }

  #cancel(event) {
    this.activePointers.delete(event.pointerId)
    this.partTapDown = null
    this.sketchTapDown = null
    this.measureTapDown = null
    if (this.sketchPinch) {
      this.sketchPinch.delete(event.pointerId)
      if (this.sketchPinch.size === 0) {
        this.sketchPinch = null
        this.renderer.setOrbitEnabled(true)
      }
    }
    if (this.sketchPointerId !== null) {
      this.sketchPointerId = null
      this.renderer.setOrbitEnabled(true)
      this.sketch?.abortStroke?.()
    }
    this.#abortDrag()
    this.#abortEdgeDrag()
    this.#abortRotateDrag()
  }

  #abortRotateDrag() {
    if (!this.rotateDrag) return
    const { clientId, startRot } = this.rotateDrag
    this.rotateDrag = null
    this.renderer.setOrbitEnabled(true)
    this.renderer.setDragHandle?.(null)
    this.renderer.setPreviewRotation(clientId, { rot_x: startRot.x, rot_y: startRot.y, rot_z: startRot.z })
  }

  #abortEdgeDrag() {
    if (!this.edgeDrag) return
    const clientId = this.edgeDrag.clientId
    this.edgeDrag = null
    this.renderer.setOrbitEnabled(true)
    this.renderer.setDragHandle?.(null)
    this.#restoreResizePreview(clientId)
  }

  #abortDrag() {
    if (!this.drag) return
    this.renderer.setDragHandle?.(null)
    // Vorschau zurücksetzen: Renderer wird beim nächsten syncScene korrigiert
    this.renderer.setPreviewPosition(this.drag.clientId, {
      pos_x: this.drag.startPart.x, pos_y: this.drag.startPart.y, pos_z: this.drag.startPart.z
    })
    this.drag = null
    this.renderer.setOrbitEnabled(true)
    this.renderer.setSnapActive?.(false)
  }
}
